import type { BakedRow } from "./catalogue";
import { rowFor, stallFor, type Stall } from "./quality";
import { placeholder } from "./spells";

export interface CartLine {
  item: string;
  /** One of the tiers the item's class offers, or absent for the stock row. */
  quality?: string;
  /** The spell written on a grimoire page. */
  spell?: string;
  quantity: number;
}

export function quantityOf(lines: CartLine[], item: string): number {
  return lines.find((line) => line.item === item)?.quantity ?? 0;
}

export function qualityOf(lines: CartLine[], item: string): string | undefined {
  return lines.find((line) => line.item === item)?.quality;
}

/**
 * A second row of the same item would stack a bonus the quantity form fires once, so a
 * repeat add steps the quantity. `count` is how many of the row one purchase is, which the
 * curation states on every row where it is more than one.
 */
export function add(
  lines: CartLine[],
  item: string,
  count = 1,
  spell?: string,
  quality?: string,
): CartLine[] {
  const held = quantityOf(lines, item);
  if (held > 0) return setQuantity(lines, item, held + count);
  const line: CartLine = { item, quantity: count };
  if (quality !== undefined) line.quality = quality;
  if (spell !== undefined) line.spell = spell;
  return [...lines, line];
}

export function remove(lines: CartLine[], item: string): CartLine[] {
  return lines.filter((line) => line.item !== item);
}

export function setQuantity(lines: CartLine[], item: string, quantity: number): CartLine[] {
  if (quantity < 1) return remove(lines, item);
  return lines.map((line) => (line.item === item ? { ...line, quantity } : line));
}

/**
 * Quality is a single-select on the item, and Add is the only control that buys. Selecting
 * a tier on an item the cart does not hold leaves the cart as it stands; the selection is
 * held on the surface until Add consumes it.
 */
export function setQuality(
  lines: CartLine[],
  item: string,
  quality: string | undefined,
): CartLine[] {
  const held = lines.find((line) => line.item === item);
  if (held === undefined || held.quality === quality) return lines;
  const { quality: _was, ...rest } = held;
  const moved = quality === undefined ? rest : { ...rest, quality };
  return lines.map((line) => (line.item === item ? moved : line));
}

export function spellOf(lines: CartLine[], item: string): string | undefined {
  return lines.find((line) => line.item === item)?.spell;
}

/** Naming the spell writes it onto a page already held. Buying the page is the Add control. */
export function setSpell(lines: CartLine[], item: string, spell: string): CartLine[] {
  return lines.map((line) => (line.item === item ? { ...line, spell } : line));
}

/** The stock price GCS wrote, at the count asked for. */
export function price(row: BakedRow, quantity: number): number {
  return (row.calc.value ?? 0) * Math.max(quantity, 1);
}

/** What the budget falls by, which on a row a discounting shop stocks is the price GCS
 *  baked with that shop's modifier attached. */
export function charged(row: BakedRow, quantity: number): number {
  const offered = row.discount_value;
  if (offered === undefined) return price(row, quantity);
  return offered * Math.max(quantity, 1);
}

export function discounted(row: BakedRow): boolean {
  return row.discount_value !== undefined;
}

/** The pounds GCS wrote on the row, at the count asked for, so a row priced as four weighs
 *  as four. */
export function pounds(row: BakedRow, quantity: number): number {
  const written = parseFloat(row.calc.weight ?? "");
  return (Number.isNaN(written) ? 0 : written) * Math.max(quantity, 1);
}

export function poundsLabel(weight: number): string {
  const written = weight.toFixed(2).replace(/\.?0+$/, "");
  return `${written === "" ? "0" : written} lb`;
}

/** The lever names the arrived rows left at home. Ticking one takes its id back off. */
export function setEquipped(unequipped: string[], id: string, equipped: boolean): string[] {
  if (equipped) return unequipped.filter((left) => left !== id);
  return unequipped.includes(id) ? unequipped : [...unequipped, id];
}

/** The cart as the splice takes it, with every line's harvested row verbatim. */
export function spliceInput(
  lines: CartLine[],
  unequipped: string[],
  stalls: Stall[],
): string {
  return JSON.stringify({
    lines: lines.map((line) => {
      const stall = stallFor(stalls, line.item);
      if (!stall) throw new Error(`${line.item} is not in this character's catalogue`);
      return { row: written(rowFor(stall, line.quality), line.spell), quantity: line.quantity };
    }),
    unequipped,
  });
}

/**
 * The row as it is bought. A page that names no spell is refused here: GCS charges the
 * full price for one, gives the bonus to nothing, and says nothing about it.
 */
function written(row: BakedRow, spell: string | undefined): BakedRow {
  const key = placeholder(row);
  if (key === null) return row;
  if (spell === undefined) {
    throw new Error(`${row.description} names no spell, and a page naming no spell buys nothing`);
  }
  return { ...row, replacements: { [key]: spell } };
}
